import { JSONPath } from 'jsonpath-plus';
import { Log } from '../../AuxillaryService/Log';
import {
    getPlaylist,
    GetPlaylistResponse,
    PlaylistItem,
    ReachVideoAssetValidator,
    YoutubeAssetValidator,
    AnnouncementAssetValidator,
    ImageAssetValidator,
    VimeoAssetValidator,
    AnnouncementAsset,
} from '../../ReachApiV2/endpoints';
import { Deferred } from '../../Utility/Deferred';
import { isNullish } from '../../Utility/Nullish';
import { shuffleArray } from '../../Utility/Shuffle';
import { Announcement } from './Announcement';
import { AnnouncementController } from './AnnouncementController';
import {
    AnnouncementReachVideo,
    AnnouncementYoutube,
    AnnouncementVimeo,
    AnnouncementDefault,
} from './Announcements';
import { AnnouncementWidget } from './AnnouncementWidget';
import { Permission, platform } from '../../Platform';
import { AnnouncementState, INSUFFICIENT_PLAYLIST_RIGHTS, VIDEO_NOT_SUPPORTED } from './types';
import { AxiosError } from 'axios';

export class AnnouncementGroup {
    public constructor(controller: AnnouncementController, widget: AnnouncementWidget) {
        this.#controller = controller;
        this.#widget = widget;
    }

    #controller: AnnouncementController;
    readonly #widget: AnnouncementWidget;

    /**
     * last announcement order
     * Annoyingly named 'id', has nothing to do with id, but rather stores
     * the value of the previous `moveNext` returned announcement's order
     * @TODO: make private
     */
    public announcementsLastOrderId = 0;

    /**
     * Collection of announcements this widget
     * has retrieved for playing on the signage
     */
    public manifest: Announcement[] = [];

    /**
     * hash of the last query
     */
    #lastQuery = '0';

    /**
     * Deferred object to handle manifest query
     */
    #queryManifestDeferred?: Deferred<GetPlaylistResponse>;

    #changed = false;
    #firstRun = true;

    public get isChanged(): boolean {
        return this.#changed;
    }

    resetChanged(): void {
        this.#changed = false;
    }

    async processUpdateActions(): Promise<void> {
        await this.updateManifest();

        await Promise.allSettled(
            this.manifest.map(async (announcement) => {
                if (announcement.state === AnnouncementState.UNPROVISIONED) {
                    await announcement.provision();
                } else {
                    // Delay the re-provisioning of any announcement that is
                    // currently being displayed.
                    announcement.needsReProvisioning = true;
                }
            })
        );
    }

    processJsonResponse(manifestJson: GetPlaylistResponse): undefined | Announcement[] {
        if (
            isNullish(manifestJson.hash) ||
            manifestJson.hash === this.#lastQuery ||
            manifestJson.announcements === undefined
        ) {
            return undefined;
        }

        //update lastQuery
        this.#lastQuery = manifestJson.hash;

        const zoneElement = document.getElementById(`zone_${this.#widget.zoneId}`);

        const width = !isNullish(this.#widget.config.width)
            ? parseInt(this.#widget.config.width)
            : zoneElement?.offsetWidth || //default to zone offset width if widget config width not set
              NaN;
        const height = !isNullish(this.#widget.config.height)
            ? parseInt(this.#widget.config.height)
            : zoneElement?.offsetHeight || //default to zone offset height if widget config height not set
              NaN;

        this.#controller.setWidgetScale(width, height);

        if (this.#widget.config.filter_expression) {
            const filterExpression = this.#widget.config.filter_expression;

            manifestJson.announcements = manifestJson.announcements.filter((row) => {
                // The function returns an empty array
                // if the evaluated expression is false
                const data = JSONPath({
                    path: filterExpression,
                    json: row,
                });

                return data.length > 0;
            });
        }

        if (this.#widget.config.transform_method) {
            try {
                if (this.#widget.config.transform_method) {
                    //disable eslint otherwise it will change let to const
                    /* eslint-disable */
                    let transformMethod!: <T>(ann: Array<T>) => Array<T>;
                    /* eslint-enable */

                    eval('transformMethod=' + this.#widget.config.transform_method + ';');
                    manifestJson.announcements =
                        transformMethod(manifestJson.announcements) || manifestJson.announcements;
                }
            } catch (e) {
                Log.Error('Failed to execute transform method', null, __fileline, e);
            }
        }

        const elements: Announcement[] = [];

        for (const announcementConfiguration of manifestJson.announcements) {
            try {
                let announcementConfigurationShallowCopy;
                const isVideoNotSupported =
                    !platform().hasPermission(Permission.PLAY_REACH_VIDEO) &&
                    announcementConfiguration.template === 'video';
                if (isVideoNotSupported) {
                    Log.Warning('Video is not supported on this platform', null, __fileline);
                    announcementConfigurationShallowCopy = {
                        ...announcementConfiguration,
                        template: VIDEO_NOT_SUPPORTED,
                        goFullScreen: false,
                        annSeconds: 10,
                    };
                } else {
                    announcementConfigurationShallowCopy = {
                        ...announcementConfiguration,
                    };
                }
                const announcement = this.createAnnouncement(
                    announcementConfigurationShallowCopy,
                    width,
                    height
                );
                elements.push(announcement);
            } catch (e) {
                Log.Error(
                    'Failed to parse announcement: ' + String(e),
                    'SIGNAGE_ANNOUNCEMENT_PARSE_ERROR',
                    __fileline,
                    { error: e, announcement: announcementConfiguration }
                );
            }
        }

        return elements;
    }

    /**
     * Infer which type of announcement class is appropriate for the configuration and then return
     * an instance of a specific announcement type.
     */
    createAnnouncement(
        configuration: PlaylistItem,
        defaultWidth: number,
        defaultHeight: number
    ): Announcement {
        if (
            configuration.template === VIDEO_NOT_SUPPORTED ||
            configuration.template === INSUFFICIENT_PLAYLIST_RIGHTS
        ) {
            return new AnnouncementDefault(
                AnnouncementAssetValidator.parse({
                    ...configuration,
                    images: [],
                    textboxes: [],
                    shapes: [],
                }),
                this.#widget
            );
        }
        switch (configuration.type) {
            case 'video':
                return new AnnouncementReachVideo(
                    ReachVideoAssetValidator.parse(configuration),
                    this.#widget
                );

            case 'youtube':
                return new AnnouncementYoutube(
                    YoutubeAssetValidator.parse(configuration),
                    this.#widget,
                    defaultWidth,
                    defaultHeight
                );
            case 'vimeo':
                return new AnnouncementVimeo(
                    VimeoAssetValidator.parse(configuration),
                    this.#widget,
                    defaultWidth,
                    defaultHeight
                );

            case 'announcement':
                return new AnnouncementDefault(
                    AnnouncementAssetValidator.parse(configuration),
                    this.#widget
                );
            case 'image':
                return new AnnouncementDefault(
                    ImageAssetValidator.parse(configuration),
                    this.#widget
                );

            default:
                throw new Error(`Announcement type not supported.`);
        }
    }

    public liftProvisionLocks(): void {
        this.manifest.forEach((announcement) => {
            announcement.provisionIsLocked = false;
        });
    }

    public moveNext(): undefined | Announcement {
        const nextItem = this.peekNext();
        if (nextItem) {
            this.announcementsLastOrderId = nextItem.order();
        }

        return nextItem;
    }

    public peekNext(): undefined | Announcement {
        let sortedManifest = this.manifest.slice().sort((playlistItemA, playlistItemB) => {
            return playlistItemA.order() - playlistItemB.order();
        });

        const currentItemIndex = sortedManifest.findIndex(
            (playlistItem) => playlistItem.order() === this.announcementsLastOrderId
        );
        if (currentItemIndex !== -1) {
            //Order sortedManifest so that it begins with the current playlistItem and
            //automatically rolls around if there are no items with greater order
            //EX: currentItemIndex = 2 then [1, 2, 3, 4, 5] --> [4, 5, 1, 2, 3]
            const sliceAt =
                currentItemIndex >= sortedManifest.length - 1 ? 0 : currentItemIndex + 1;
            sortedManifest = sortedManifest.slice(sliceAt).concat(sortedManifest.slice(0, sliceAt));
        }

        for (let i = 0; i < sortedManifest.length; i++) {
            const playlistItem = sortedManifest[i];
            if (playlistItem.canPlay()) {
                return playlistItem;
            }
        }

        return undefined;
    }

    public async deprovision(): Promise<void> {
        const stopPromises = this.manifest.map((announcement) => announcement.halt());
        await Promise.all(stopPromises);
    }

    async updateManifest(): Promise<void> {
        try {
            const manifest = await this.getManifest();
            await this.merge(this.processJsonResponse(manifest));
        } catch (error) {
            Log.Error(
                `Failed to updated manifest for playlist ${this.#widget.config.screenid}`,
                null,
                __fileline,
                error
            );

            if (error instanceof AxiosError && !isNullish(error.status) && error.status >= 400) {
                const errorAnnouncements: AnnouncementAsset[] = [
                    {
                        announcementId: String(this.#widget.id),
                        dateStart: '1970-01-01',
                        dateEnd: '2099-12-31',
                        scheduleParts: [
                            {
                                runDays: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat',
                                runStartTime: '12:00 AM',
                                runEndTime: '11:59 PM',
                            },
                        ],
                        annStatus: 'active',
                        goFullScreen: false,
                        annSeconds: 10,
                        announcementOrder: 1,
                        updatedTimestamp: null,
                        groupId: null,
                        announcementName: 'Playlist Error',
                        template: 'insufficient-playlist-rights',
                        type: 'Announcement',
                        backgroundColor: '',
                        width: 1920,
                        height: 1080,
                        images: [],
                        textboxes: [],
                        shapes: [],
                    },
                ];
                const errorManifest: GetPlaylistResponse = {
                    hash: '1',
                    errors: [{ httpCode: error.status, message: error.message }],
                    announcements: errorAnnouncements,
                };

                await this.merge(this.processJsonResponse(errorManifest));
            }
        } finally {
            this.#queryManifestDeferred = undefined;
        }
    }

    async merge(newManifest?: Announcement[]): Promise<void> {
        if (!newManifest) {
            //no changes present on server, return
            return;
        }

        const merged: Announcement[] = [];

        for (let i = 0; i <= newManifest.length - 1; ++i) {
            //shift one off the new manifest array
            const newAnnouncement = newManifest[i];

            let found = false;
            for (let i = 0; i < this.manifest.length; ++i) {
                if (
                    this.manifest[i].configuration.announcementId ===
                    newAnnouncement.configuration.announcementId
                ) {
                    //found match. Update existing announcement with new announcement
                    const existingAnnouncement = this.manifest[i];

                    this.#changed = existingAnnouncement.update(newAnnouncement) || !this.#firstRun;
                    merged.push(existingAnnouncement);
                    found = true;

                    //remove element from array
                    this.manifest.splice(i, 1);

                    break;
                }
            }

            if (!found) {
                //new announcement not found, add to merged
                this.#changed = !this.#firstRun;
                merged.push(newAnnouncement);
            }
        }

        //remaining elements have not been modified
        await Promise.allSettled(
            this.manifest.map(async (announcement) => {
                await announcement.safeUnprovision();
            })
        );

        this.manifest = merged.sort((a, b) => {
            return a.sortOrder - b.sortOrder;
        });

        if (this.#widget.config.shuffle) {
            const shuffled = shuffleArray(
                AnnouncementGroup.createGroupings(this.manifest.slice())
            ).flat();

            this.manifest.length = 0;
            this.manifest.push(...shuffled);
        }

        //it is possible to have multiple announcements with the same order ID in
        //CMS, so we need to set them ourselves
        for (let i = 0; i < this.manifest.length; i++) {
            this.manifest[i].order(i + 1);
        }

        Log.Info('CHANGES MERGED!', null, __fileline);

        this.#firstRun = false;
    }

    static createGroupings(playlistItems: Announcement[]): (Announcement | Announcement[])[] {
        const groupManifest: Record<string, Announcement[]> = {};
        const groupedPlaylistItems: (Announcement | Announcement[])[] = [];

        playlistItems.forEach((playlistItem) => {
            if (playlistItem.configuration.groupId) {
                const groupId = playlistItem.configuration.groupId;

                if (Object.prototype.hasOwnProperty.call(groupManifest, groupId)) {
                    groupManifest[groupId].push(playlistItem);
                } else {
                    groupManifest[groupId] = [playlistItem];
                    groupedPlaylistItems.push(groupManifest[groupId]);
                }
            } else {
                groupedPlaylistItems.push(playlistItem);
            }
        });

        return groupedPlaylistItems;
    }

    async getManifest(): Promise<GetPlaylistResponse> {
        if (this.#queryManifestDeferred) {
            return this.#queryManifestDeferred.promise;
        }

        this.#queryManifestDeferred = new Deferred();
        const result = await getPlaylist({
            playlistId: this.#widget.config.screenid,
            lastQueryHash: this.#lastQuery,
        });
        this.#queryManifestDeferred.resolve(result);
        return result;
    }
}
